import { useState, useEffect, useCallback } from 'react'
import { useAuthStore, API_BASE, apiFetch } from '../store/auth'

export interface OnboardingStep {
  key: string
  title: string
  description: string
  sort_order: number
}

export interface OnboardingProgress {
  id: string
  organization_id: string
  current_step: string
  step_fiscal_period_completed: number
  step_chart_of_accounts_completed: number
  step_first_transaction_completed: number
  completed_at: string | null
  org_type: string | null
  ai_interactions: number
  skipped_steps: string[]
  progress_percent: number
}

export interface OnboardingState {
  progress: OnboardingProgress | null
  steps: OnboardingStep[]
  loading: boolean
  error: string | null
  isComplete: boolean
}

export function useOnboarding() {
  const { activeOrg } = useAuthStore()
  const [state, setState] = useState<OnboardingState>({
    progress: null,
    steps: [],
    loading: false,
    error: null,
    isComplete: false,
  })

  const fetchProgress = useCallback(async () => {
    if (!activeOrg) return
    setState(s => ({ ...s, loading: true, error: null }))
    try {
      const res = await apiFetch(`${API_BASE}/onboarding/progress?organization_id=${activeOrg.id}`)
      if (!res.ok) throw new Error('Failed to fetch onboarding progress')
      const data = await res.json() as {
        progress: OnboardingProgress
        steps: OnboardingStep[]
        recommended_next_action: string | null
      }
      setState({
        progress: data.progress,
        steps: data.steps,
        loading: false,
        error: null,
        isComplete: !!data.progress.completed_at,
      })
    } catch (e) {
      setState(s => ({ ...s, loading: false, error: String(e) }))
    }
  }, [activeOrg])

  useEffect(() => {
    fetchProgress()
  }, [fetchProgress])

  const completeStep = useCallback(async (step: string, skip = false) => {
    if (!activeOrg) return
    const res = await apiFetch(`${API_BASE}/onboarding/complete-step`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_id: activeOrg.id, step, skip }),
    })
    if (!res.ok) {
      const err = await res.json() as { error?: string }
      throw new Error(err.error || 'Failed to complete step')
    }
    await fetchProgress()
    return await res.json()
  }, [activeOrg, fetchProgress])

  const updateOrgType = useCallback(async (orgType: string) => {
    if (!activeOrg) return
    await apiFetch(`${API_BASE}/onboarding/update-org-type`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_id: activeOrg.id, org_type: orgType }),
    })
    setState(s => ({
      ...s,
      progress: s.progress ? { ...s.progress, org_type: orgType } : s.progress,
    }))
  }, [activeOrg])

  const sendAiMessage = useCallback(async (
    message: string,
    conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>
  ): Promise<{ response: string; tools_used: string[]; current_step: string }> => {
    if (!activeOrg) throw new Error('No organization')
    const res = await apiFetch(`${API_BASE}/onboarding/ai-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        organization_id: activeOrg.id,
        message,
        conversation_history: conversationHistory,
      }),
    })
    if (!res.ok) {
      const err = await res.json() as { error?: string }
      throw new Error(err.error || 'AI error')
    }
    const data = await res.json() as { response: string; tools_used: string[]; current_step: string }
    // Refresh progress in case AI completed a step
    await fetchProgress()
    return data
  }, [activeOrg, fetchProgress])

  const executeAiAction = useCallback(async (action: string, params: Record<string, unknown>) => {
    if (!activeOrg) throw new Error('No organization')
    const res = await apiFetch(`${API_BASE}/onboarding/ai-action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_id: activeOrg.id, action, params }),
    })
    if (!res.ok) {
      const err = await res.json() as { error?: string }
      throw new Error(err.error || 'Action failed')
    }
    await fetchProgress()
    return await res.json()
  }, [activeOrg, fetchProgress])

  return {
    ...state,
    fetchProgress,
    completeStep,
    updateOrgType,
    sendAiMessage,
    executeAiAction,
  }
}
